import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { CmmdUserForgeAutomationRun, EnvironmentId } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { createCmmdApiClient } from "~/environments/cmmdApi";
import { usePrimaryEnvironmentDescriptor } from "~/environments/primary";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { usePublishBreadcrumbLabel } from "~/components/breadcrumb/globalBreadcrumbStore";
import { AutomationLoadFailure } from "./AutomationLoadFailure";
import { ForgeAutomationDetail } from "./ForgeAutomationDetail";
import { itemName, type AutomationListItem } from "./ForgeAutomationModel";
import { cloudDisplayRun, localDisplayRun } from "./automationRunModel";
import { useForgeAutomationsIndex } from "./useForgeAutomationsIndex";

type AutomationsIndexState = ReturnType<typeof useForgeAutomationsIndex>;

function findAutomationItem(
  state: AutomationsIndexState,
  cloudId: number | null,
  localId: string | null,
  cloudRuns: readonly CmmdUserForgeAutomationRun[],
): AutomationListItem | null {
  const cloudRule = state.cloudAutomations.find((rule) => rule.id === cloudId);
  if (cloudRule) {
    return { kind: "cloud", key: `cloud:${cloudRule.id}`, automation: cloudRule, runs: cloudRuns };
  }
  const localAutomation = state.localAutomations.find((automation) => automation.id === localId);
  if (localAutomation) {
    return {
      kind: "local",
      key: `local:${localAutomation.id}`,
      automation: localAutomation,
      runs: state.localRunsByAutomation[localAutomation.id] ?? [],
    };
  }
  return null;
}

function loadErrorForRoute(
  state: AutomationsIndexState,
  cloudId: number | null,
  localId: string | null,
): string | null {
  if (cloudId !== null) return state.cloudAutomationsLoadError;
  if (localId !== null) return state.localAutomationsLoadError;
  return null;
}

interface LoadedAutomationDetailProps {
  readonly cloudRunsError: string | null;
  readonly cloudRunsLoading: boolean;
  readonly environmentId: EnvironmentId | null;
  readonly item: AutomationListItem;
  readonly state: AutomationsIndexState;
  readonly onCloudRunsChanged: () => void;
}

function LoadedAutomationDetail({
  cloudRunsError,
  cloudRunsLoading,
  environmentId,
  item,
  onCloudRunsChanged,
  state,
}: LoadedAutomationDetailProps) {
  const navigate = useNavigate();
  const runs =
    item.kind === "cloud"
      ? item.runs.map(cloudDisplayRun)
      : item.runs.map((run) => localDisplayRun(run, environmentId));
  const projectNames = new Map(
    state.projects.map((project) => [String(project.id), project.title]),
  );

  async function archive() {
    const updated =
      item.kind === "cloud"
        ? await state.updateCloudAutomation(item.automation.id, { status: "archived" })
        : await state.updateLocalAutomation({ id: item.automation.id, status: "archived" });
    if (!updated) return;
    await navigate({ to: "/automations" });
  }

  function toggle() {
    const status = item.automation.status === "active" ? "paused" : "active";
    if (item.kind === "cloud") {
      void state.updateCloudAutomation(item.automation.id, { status });
      return;
    }
    void state.updateLocalAutomation({ id: item.automation.id, status });
  }

  function runNow() {
    if (item.kind === "cloud") {
      void state.runCloudAutomation(item.automation.id).then((started) => {
        if (started) onCloudRunsChanged();
      });
      return;
    }
    void state.runLocalAutomation(item.automation.id);
  }

  return (
    <ForgeAutomationDetail
      item={item}
      projectNames={projectNames}
      runs={runs}
      busy={state.updatingAutomationKey === item.key}
      error={state.error}
      runsError={cloudRunsError}
      runsLoading={item.kind === "cloud" && cloudRunsLoading}
      onArchive={() => void archive()}
      onRetryRuns={onCloudRunsChanged}
      onRunNow={runNow}
      onToggle={toggle}
    />
  );
}

export function ForgeAutomationDetailPage({ automationId }: { readonly automationId: string }) {
  const state = useForgeAutomationsIndex();
  const descriptor = usePrimaryEnvironmentDescriptor();
  const cloudId = automationId.startsWith("cloud-") ? Number(automationId.slice(6)) : null;
  const localId = automationId.startsWith("local-") ? automationId.slice(6) : null;
  const [cloudRuns, setCloudRuns] = useState<readonly CmmdUserForgeAutomationRun[]>([]);
  const [cloudRunsError, setCloudRunsError] = useState<string | null>(null);
  const [cloudRunsLoading, setCloudRunsLoading] = useState(cloudId !== null);
  const [cloudRunsNonce, setCloudRunsNonce] = useState(0);

  useEffect(() => {
    let active = true;
    if (cloudId === null) {
      setCloudRunsLoading(false);
      return () => {
        active = false;
      };
    }
    setCloudRunsLoading(true);
    setCloudRunsError(null);
    void createCmmdApiClient()
      .listUserForgeAutomationRuns(cloudId)
      .then((runs) => {
        if (active) {
          setCloudRuns(runs);
          setCloudRunsError(null);
          setCloudRunsLoading(false);
        }
      })
      .catch((cause: unknown) => {
        if (active) {
          setCloudRunsError(sanitizePublicUiError(cause, "Unable to load run history."));
          setCloudRunsLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [cloudId, cloudRunsNonce]);

  // Resolved before the early returns so the hook below is unconditional. The
  // automation's name is only reachable through this page's own index hook, so
  // the global bar cannot derive it — the page hands it over instead.
  const routeItem = findAutomationItem(state, cloudId, localId, cloudRuns);
  usePublishBreadcrumbLabel("automation-detail", routeItem === null ? null : itemName(routeItem));

  if (state.loading) return <main className="min-h-0 flex-1 bg-background" />;
  const loadError = loadErrorForRoute(state, cloudId, localId);
  if (loadError) return <AutomationLoadFailure error={loadError} onRetry={state.refresh} />;
  const item = routeItem;
  if (!item) {
    return (
      <main className="grid min-h-0 flex-1 place-items-center px-4 text-center">
        <div>
          <h1 className="text-lg font-semibold">Automation not found</h1>
          <Button
            className={RESPONSIVE_TEXT_BUTTON_CLASS}
            render={<Link to="/automations" />}
            variant="ghost"
          >
            Back to automations
          </Button>
        </div>
      </main>
    );
  }
  return (
    <LoadedAutomationDetail
      cloudRunsError={cloudRunsError}
      cloudRunsLoading={cloudRunsLoading}
      environmentId={descriptor?.environmentId ?? null}
      item={item}
      state={state}
      onCloudRunsChanged={() => setCloudRunsNonce((value) => value + 1)}
    />
  );
}
